import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Store, TrendingUp, Users, IndianRupee, CheckCircle, Loader2, Phone, Mail, MapPin, Search, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// All Indian cities list for search
const ALL_INDIAN_CITIES = [
  "Agra", "Ahmedabad", "Ajmer", "Aligarh", "Allahabad", "Amravati", "Amritsar", "Aurangabad",
  "Bangalore", "Bareilly", "Belgaum", "Bhilai", "Bhiwandi", "Bhopal", "Bhubaneswar", "Bikaner",
  "Chandigarh", "Chennai", "Coimbatore", "Cuttack", "Dehradun", "Delhi", "Dhanbad", "Durgapur",
  "Faridabad", "Firozabad", "Ghaziabad", "Gorakhpur", "Gulbarga", "Guntur", "Gurgaon", "Guwahati",
  "Gwalior", "Howrah", "Hubli-Dharwad", "Hyderabad", "Indore", "Jabalpur", "Jaipur", "Jalandhar",
  "Jammu", "Jamnagar", "Jamshedpur", "Jhansi", "Jodhpur", "Jajpur", "Kakinada", "Kalyan-Dombivali", "Kanpur",
  "Kochi", "Kolhapur", "Kolkata", "Kota", "Kozhikode", "Kurnool", "Lucknow", "Ludhiana",
  "Madurai", "Maheshtala", "Malegaon", "Mangalore", "Meerut", "Moradabad", "Mumbai", "Mysore",
  "Nagpur", "Nanded", "Nashik", "Navi Mumbai", "Nellore", "New Delhi", "Noida", "Patna",
  "Pimpri-Chinchwad", "Pondicherry", "Pune", "Raipur", "Rajahmundry", "Rajkot", "Ranchi", "Rourkela",
  "Saharanpur", "Salem", "Sangli", "Shimla", "Siliguri", "Solapur", "Srinagar", "Surat",
  "Thane", "Thiruvananthapuram", "Thrissur", "Tiruchirappalli", "Tiruppur", "Udaipur", "Ujjain",
  "Ulhasnagar", "Vadodara", "Varanasi", "Vasai-Virar", "Vijayawada", "Visakhapatnam", "Warangal"
];

const CUISINE_TYPES = [
  "North Indian", "South Indian", "Chinese", "Italian", "Continental", 
  "Street Food", "Fast Food", "Mughlai", "Bengali", "Punjabi",
  "Rajasthani", "Gujarati", "Kerala", "Hyderabadi", "Chettinad",
  "Thai", "Japanese", "Mexican", "Mediterranean", "Lebanese",
  "Desserts", "Bakery", "Beverages", "Ice Cream", "Healthy Food",
  "Biryani", "Pizza", "Burger", "Rolls", "Momos", "Seafood"
];

const BUSINESS_TYPES = [
  { value: "restaurant", label: "Restaurant" },
  { value: "food_truck", label: "Food Truck" },
  { value: "cloud_kitchen", label: "Cloud Kitchen" }
];

export const RestaurantPartner = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('partner');
  const [citySearch, setCitySearch] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [filteredCities, setFilteredCities] = useState([]);
  
  // Modal states
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [partnerId, setPartnerId] = useState('');
  const [otp, setOtp] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  
  const [formData, setFormData] = useState({
    restaurant_name: '',
    owner_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    pincode: '',
    business_type: '',
    cuisine_types: [],
    partnership_type: 'partner',
  });

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  useEffect(() => {
    if (citySearch.length > 0) {
      const filtered = ALL_INDIAN_CITIES.filter(city => 
        city.toLowerCase().includes(citySearch.toLowerCase())
      );
      setFilteredCities(filtered);
      setShowCityDropdown(true);
    } else {
      setFilteredCities([]);
      setShowCityDropdown(false);
    }
  }, [citySearch]);

  const handleChange = (e) => {
    const value = e.target.value;
    const name = e.target.name;
    
    if (name === 'phone' || name === 'pincode') {
      if (!/^\d*$/.test(value)) return;
    }
    
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleCitySelect = (city) => {
    setFormData({ ...formData, city });
    setCitySearch(city);
    setShowCityDropdown(false);
  };

  const handleCuisineToggle = (cuisine) => {
    const current = formData.cuisine_types;
    if (current.includes(cuisine)) {
      setFormData({ ...formData, cuisine_types: current.filter(c => c !== cuisine) });
    } else if (current.length < 5) {
      setFormData({ ...formData, cuisine_types: [...current, cuisine] });
    } else {
      toast.error('You can select maximum 5 cuisine types');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.cuisine_types.length < 3) {
      toast.error('Please select at least 3 cuisine types');
      return;
    }
    
    if (formData.phone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    
    if (formData.pincode.length !== 6) {
      toast.error('Please enter a valid 6-digit pincode');
      return;
    }
    
    // Show Agreement Modal first
    setShowAgreementModal(true);
  };

  const handleAgreementAccept = () => {
    if (!agreementAccepted) {
      toast.error('Please accept the terms and conditions');
      return;
    }
    setShowAgreementModal(false);
    setAgreementAccepted(false); // Reset for summary modal
    setShowSummaryModal(true);
  };

  const handleSummaryAccept = async () => {
    if (!agreementAccepted) {
      toast.error('Please accept the agreement summary');
      return;
    }
    setShowSummaryModal(false);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/partners/restaurant`, {
        ...formData,
        partnership_type: activeTab,
      });
      
      setPartnerId(response.data.id);
      
      // Send OTP
      const otpResponse = await axios.post(`${API}/auth/send-otp`, {
        phone: formData.phone,
      });
      
      toast.success('Application submitted! Please verify your phone number.');
      
      // Show demo OTP for testing
      if (otpResponse.data.demo_otp) {
        toast.info(`Demo OTP: ${otpResponse.data.demo_otp}`, {
          duration: 30000,
          description: 'Use this OTP for testing'
        });
      }
      
      setShowOtpModal(true);
      setCountdown(60);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }
    
    setOtpLoading(true);
    
    try {
      await axios.post(`${API}/partners/restaurant/verify`, {
        partner_id: partnerId,
        phone: formData.phone,
        otp: otp,
      });
      
      toast.success('Phone verified successfully! Redirecting to menu setup...');
      setShowOtpModal(false);
      
      // Store partner info for menu page
      localStorage.setItem('fudco_restaurant_partner', JSON.stringify({
        id: partnerId,
        name: formData.restaurant_name,
        phone: formData.phone
      }));
      
      // Redirect to add menu page
      navigate('/restaurant-partner/add-menu');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (countdown > 0) return;
    
    try {
      const otpResponse = await axios.post(`${API}/auth/send-otp`, {
        phone: formData.phone,
      });
      toast.success('OTP resent successfully!');
      setCountdown(60);
      
      if (otpResponse.data.demo_otp) {
        toast.info(`Demo OTP: ${otpResponse.data.demo_otp}`, {
          duration: 30000,
          description: 'Use this OTP for testing'
        });
      }
    } catch (error) {
      toast.error('Failed to resend OTP. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Hero Section */}
      <section className="fudco-section bg-[#0A2F0A] text-white py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-[#FFD700]"></div>
          <div className="absolute bottom-10 right-20 w-48 h-48 rounded-full bg-[#2D8B2E]"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Partner with <span className="text-[#FFD700]">fuDco</span>
              </h1>
              <p className="text-xl text-gray-300 mb-6">
                Grow your restaurant business with India's first food stock market
              </p>
              <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                  <Users className="w-5 h-5 text-[#FFD700]" />
                  <span>10,000+ Orders Daily</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                  <Store className="w-5 h-5 text-[#FFD700]" />
                  <span>500+ Restaurant Partners</span>
                </div>
              </div>
            </div>
            
            <div className="flex-1 grid grid-cols-2 gap-4 max-w-md">
              <Card className="bg-white/10 backdrop-blur p-4 text-center border-0">
                <TrendingUp className="w-8 h-8 text-[#FFD700] mx-auto mb-2" />
                <p className="text-2xl font-bold">30%</p>
                <p className="text-sm text-gray-300">Revenue Growth</p>
              </Card>
              <Card className="bg-white/10 backdrop-blur p-4 text-center border-0">
                <IndianRupee className="w-8 h-8 text-[#FFD700] mx-auto mb-2" />
                <p className="text-2xl font-bold">₹2L+</p>
                <p className="text-sm text-gray-300">Avg Monthly Earnings</p>
              </Card>
              <Card className="bg-white/10 backdrop-blur p-4 text-center border-0 col-span-2">
                <CheckCircle className="w-8 h-8 text-[#FFD700] mx-auto mb-2" />
                <p className="text-lg font-bold">Quick Onboarding</p>
                <p className="text-sm text-gray-300">Start receiving orders in 24 hours</p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section className="fudco-section py-12 px-4">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
          <Card className="p-8 shadow-xl border-0">
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-bold text-[#0A2F0A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Restaurant Registration
              </h2>
              <p className="text-gray-600">Fill in your details to start partnering with Fudco</p>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-8">
                <TabsTrigger value="partner" className="data-[state=active]:bg-[#2D8B2E] data-[state=active]:text-white">
                  Partner With Us
                </TabsTrigger>
                <TabsTrigger value="consulting" className="data-[state=active]:bg-[#2D8B2E] data-[state=active]:text-white">
                  Restaurant Consulting
                </TabsTrigger>
              </TabsList>

              <TabsContent value="partner" className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="restaurant_name">Restaurant Name *</Label>
                    <Input
                      id="restaurant_name"
                      name="restaurant_name"
                      placeholder="Enter restaurant name"
                      value={formData.restaurant_name}
                      onChange={handleChange}
                      required
                      className="h-12 rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="owner_name">Owner Name *</Label>
                    <Input
                      id="owner_name"
                      name="owner_name"
                      placeholder="Enter owner name"
                      value={formData.owner_name}
                      onChange={handleChange}
                      required
                      className="h-12 rounded-xl"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Enter email address"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="h-12 rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <div className="flex">
                      <div className="flex items-center px-4 bg-gray-100 border border-r-0 border-gray-200 rounded-l-xl text-gray-600">
                        +91
                      </div>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="10-digit number"
                        value={formData.phone}
                        onChange={handleChange}
                        maxLength={10}
                        required
                        className="h-12 rounded-l-none rounded-r-xl"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Restaurant Address *</Label>
                  <Input
                    id="address"
                    name="address"
                    placeholder="Enter complete address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    className="h-12 rounded-xl"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2 relative">
                    <Label>City *</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search city..."
                        value={citySearch}
                        onChange={(e) => {
                          setCitySearch(e.target.value);
                          setFormData({ ...formData, city: '' });
                        }}
                        className="h-12 rounded-xl pl-10"
                      />
                    </div>
                    {showCityDropdown && filteredCities.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                        {filteredCities.slice(0, 10).map((city) => (
                          <button
                            key={city}
                            type="button"
                            onClick={() => handleCitySelect(city)}
                            className="w-full px-4 py-3 text-left hover:bg-green-50 flex items-center gap-2"
                          >
                            <MapPin className="w-4 h-4 text-[#2D8B2E]" />
                            {city}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pincode">Pincode *</Label>
                    <Input
                      id="pincode"
                      name="pincode"
                      type="text"
                      placeholder="6-digit pincode"
                      value={formData.pincode}
                      onChange={handleChange}
                      maxLength={6}
                      required
                      className="h-12 rounded-xl"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Business Type *</Label>
                  <Select onValueChange={(value) => handleSelectChange('business_type', value)}>
                    <SelectTrigger className="h-12 rounded-xl">
                      <SelectValue placeholder="Select business type" />
                    </SelectTrigger>
                    <SelectContent>
                      {BUSINESS_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <Label>Cuisine Types * (Select 3-5)</Label>
                  <div className="flex flex-wrap gap-2">
                    {CUISINE_TYPES.map((cuisine) => (
                      <button
                        key={cuisine}
                        type="button"
                        onClick={() => handleCuisineToggle(cuisine)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                          formData.cuisine_types.includes(cuisine)
                            ? 'bg-[#2D8B2E] text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {cuisine}
                      </button>
                    ))}
                  </div>
                  <p className="text-sm text-gray-500">
                    Selected: {formData.cuisine_types.length}/5
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="consulting" className="space-y-6">
                <div className="text-center py-8">
                  <Store className="w-16 h-16 text-[#2D8B2E] mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-2">Restaurant Consulting Services</h3>
                  <p className="text-gray-600 mb-6">
                    Get expert guidance to optimize your restaurant operations and increase profitability
                  </p>
                  <div className="grid md:grid-cols-3 gap-4 text-left">
                    <Card className="p-4 border-[#2D8B2E]/20">
                      <CheckCircle className="w-6 h-6 text-[#2D8B2E] mb-2" />
                      <h4 className="font-semibold">Menu Optimization</h4>
                      <p className="text-sm text-gray-600">Data-driven menu engineering</p>
                    </Card>
                    <Card className="p-4 border-[#2D8B2E]/20">
                      <CheckCircle className="w-6 h-6 text-[#2D8B2E] mb-2" />
                      <h4 className="font-semibold">Operations Review</h4>
                      <p className="text-sm text-gray-600">Streamline your kitchen workflow</p>
                    </Card>
                    <Card className="p-4 border-[#2D8B2E]/20">
                      <CheckCircle className="w-6 h-6 text-[#2D8B2E] mb-2" />
                      <h4 className="font-semibold">Marketing Support</h4>
                      <p className="text-sm text-gray-600">Boost your online presence</p>
                    </Card>
                  </div>
                </div>
                
                {/* Same form fields for consulting */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="restaurant_name">Restaurant Name *</Label>
                    <Input
                      id="restaurant_name"
                      name="restaurant_name"
                      placeholder="Enter restaurant name"
                      value={formData.restaurant_name}
                      onChange={handleChange}
                      required
                      className="h-12 rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="owner_name">Owner Name *</Label>
                    <Input
                      id="owner_name"
                      name="owner_name"
                      placeholder="Enter owner name"
                      value={formData.owner_name}
                      onChange={handleChange}
                      required
                      className="h-12 rounded-xl"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Enter email address"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="h-12 rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <div className="flex">
                      <div className="flex items-center px-4 bg-gray-100 border border-r-0 border-gray-200 rounded-l-xl text-gray-600">
                        +91
                      </div>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="10-digit number"
                        value={formData.phone}
                        onChange={handleChange}
                        maxLength={10}
                        required
                        className="h-12 rounded-l-none rounded-r-xl"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2 relative">
                    <Label>City *</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search city..."
                        value={citySearch}
                        onChange={(e) => {
                          setCitySearch(e.target.value);
                          setFormData({ ...formData, city: '' });
                        }}
                        className="h-12 rounded-xl pl-10"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pincode">Pincode *</Label>
                    <Input
                      id="pincode"
                      name="pincode"
                      type="text"
                      placeholder="6-digit pincode"
                      value={formData.pincode}
                      onChange={handleChange}
                      maxLength={6}
                      required
                      className="h-12 rounded-xl"
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 mt-8 bg-[#2D8B2E] hover:bg-[#236c24] text-white rounded-full font-semibold text-lg"
            >
              {loading ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <>
                  Submit Application
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </Card>

          {/* Contact Info */}
          <section className="mt-8 text-center">
            <p className="text-gray-600 mb-2">Need help with registration?</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="mailto:service@fudco.in" className="flex items-center gap-2 text-[#2D8B2E] hover:underline">
                <Mail className="w-4 h-4" />
                <span>service@fudco.in</span>
              </a>
            </div>
          </section>
        </form>
      </section>

      {/* Full Agreement Modal */}
      <Dialog open={showAgreementModal} onOpenChange={setShowAgreementModal}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="restaurant-agreement-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center text-[#1b5e20] border-b-2 border-[#1b5e20] pb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Restaurant Partner Agreement
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4 text-sm leading-relaxed">
            <p>
              These Terms and Conditions ("<strong>Terms</strong>") form part of the Restaurant Partner Enrolment Form for Food Ordering and
              Delivery Services ("<strong>Form</strong>") and constitute a legally binding agreement between:
            </p>
            <p>
              <strong>Traderow Food Serve Private Limited</strong>, operating under the brand name <strong>Fudco</strong>
              ("<strong>Fudco</strong>", "we", "us", "our")
              <br />AND<br />
              The restaurant, cloud kitchen, food outlet, or food business enrolling on the Fudco Platform
              ("<strong>Restaurant Partner</strong>", "you", "your").
            </p>

            <div className="mt-6">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-2">1. Definitions</h3>
              <p className="text-gray-700">
                "Platform" means the Fudco website, mobile applications, dashboards, APIs, and related technology.
                "Customer" means any user placing an order through the Platform.
                "Order" means a confirmed food order placed by a Customer via the Platform.
                "Gross Sales" means the total value of Orders including applicable taxes, less discounts (if any).
                "Net Payout" means Gross Sales minus commission, operational charges, refunds, and other applicable deductions.
              </p>
            </div>

            <div className="mt-6">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-2">2. Scope of Services</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Listing of Restaurant Partner menu and prices on the Platform</li>
                <li>Order placement and management system</li>
                <li>Customer discovery and demand generation</li>
                <li>Payment facilitation</li>
                <li>Optional logistics facilitation (if opted)</li>
                <li>Customer and partner support</li>
              </ul>
            </div>

            <div className="mt-6">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-2">3. Commission, Charges & Payout</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li><strong>Commission:</strong> Fudco shall charge a fixed <strong>15% commission</strong> on the Gross Sales of each completed Order.</li>
                <li><strong>Operational Charges:</strong> ₹<strong>5 (Rupees Five only)</strong> per Order.</li>
                <li><strong>Payout Cycle:</strong> Net Payouts shall be settled to the Restaurant Partner every <strong>6 (six) days</strong>.</li>
                <li>All deductions shall be adjusted before settlement.</li>
              </ul>
              <p className="text-gray-700 mt-2">
                Fudco reserves the right to deduct refunds, penalties, taxes, and statutory charges as applicable under law.
              </p>
            </div>

            <div className="mt-6">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-2">4. Restaurant Partner Obligations</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Ensure food quality, hygiene, and safety at all times</li>
                <li>Comply with Food Safety and Standards Act, 2006 and applicable laws</li>
                <li>Maintain valid FSSAI, GST, PAN, and local licenses</li>
                <li>Accept and process Orders promptly</li>
                <li>Provide accurate menu, pricing, and availability information</li>
                <li>Ensure proper packaging to avoid spillage or contamination</li>
                <li>Not discriminate against Customers or Delivery Partners</li>
              </ul>
            </div>

            <div className="mt-6">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-2">5. Delivery & Logistics</h3>
              <p className="text-gray-700">
                Fudco acts only as a technology facilitator. Where logistics are provided by third-party Delivery Partners,
                Fudco shall not be responsible for food quality or quantity.
              </p>
              <p className="text-gray-700 mt-2">
                Where delivery is undertaken by the Restaurant Partner, the Restaurant Partner shall be solely responsible
                for timely and safe delivery.
              </p>
            </div>

            <div className="mt-6">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-2">6. Payments & Refunds</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Payments may be made online or via cash on delivery</li>
                <li>Refunds to Customers due to Restaurant Partner fault may be deducted from payouts</li>
                <li>Disputed Orders may be withheld until resolution</li>
              </ul>
            </div>

            <div className="mt-6">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-2">7. Intellectual Property</h3>
              <p className="text-gray-700">
                Restaurant Partner grants Fudco a non-exclusive, royalty-free license to use its name, logo, menu, and content
                for listing, marketing, and promotion on the Platform.
              </p>
            </div>

            <div className="mt-6">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-2">8. Data Protection</h3>
              <p className="text-gray-700">
                Customer data shall be used only for Order fulfillment. Restaurant Partner shall not misuse, sell, or market
                to Customers without explicit consent.
              </p>
            </div>

            <div className="mt-6">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-2">9. Term & Termination</h3>
              <p className="text-gray-700">
                This Agreement shall commence from the execution date and continue until terminated.
                Either party may terminate with <strong>7 days written notice</strong>.
              </p>
              <p className="text-gray-700 mt-2">
                Fudco may immediately suspend or terminate access in case of fraud, legal non-compliance,
                food safety violations, or repeated poor customer experience.
              </p>
            </div>

            <div className="mt-6">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-2">10. Limitation of Liability</h3>
              <p className="text-gray-700">
                Fudco shall not be liable for indirect, incidental, or consequential damages.
                Restaurant Partner shall indemnify Fudco against claims arising from food quality,
                regulatory non-compliance, or Customer disputes.
              </p>
            </div>

            <div className="mt-6">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-2">11. Governing Law</h3>
              <p className="text-gray-700">
                This Agreement shall be governed by and construed in accordance with the laws of India.
                Courts of Odisha shall have exclusive jurisdiction.
              </p>
            </div>

            {/* Company Details Footer */}
            <div className="mt-8 p-5 bg-gray-100 rounded-xl border-l-4 border-[#1b5e20]">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-3">Company Details</h3>
              <p className="text-gray-700">
                <strong>Legal Entity:</strong> Traderow Food Serve Private Limited<br />
                <strong>Brand:</strong> Fudco<br />
                <strong>Registered Address:</strong><br />
                At: Baragadia, PO: Baragadia<br />
                Jajpur, Odisha – 755026, India
              </p>
              <p className="text-gray-700 mt-3">
                <strong>Email:</strong> service@fudco.in<br />
                <strong>Website:</strong> www.fudco.in
              </p>
            </div>
            
            <div className="flex items-start gap-3 p-4 bg-green-50 rounded-xl mt-6">
              <Checkbox 
                id="agreement" 
                checked={agreementAccepted}
                onCheckedChange={setAgreementAccepted}
                className="mt-1"
                data-testid="restaurant-agreement-checkbox"
              />
              <label htmlFor="agreement" className="text-sm text-gray-700 cursor-pointer">
                I have read and agree to the <strong>Restaurant Partner Agreement</strong> and the full <strong>Terms & Conditions</strong>.
              </label>
            </div>
            
            <Button
              onClick={handleAgreementAccept}
              disabled={!agreementAccepted}
              className="w-full h-12 bg-[#1b5e20] hover:bg-[#145216] text-white rounded-lg font-semibold"
              data-testid="restaurant-agreement-accept-btn"
            >
              Accept & Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Agreement Summary Modal */}
      <Dialog open={showSummaryModal} onOpenChange={setShowSummaryModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="restaurant-summary-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center text-[#1b5e20]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Fudco – Restaurant Partner Agreement Summary
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            <p className="text-gray-700">
              By partnering with <strong>Fudco</strong> (operated by <strong>Traderow Food Serve Private Limited</strong>),
              you agree to the following terms:
            </p>

            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-[#1b5e20] flex-shrink-0 mt-0.5" />
                <span><strong>Commission:</strong> 15% on every completed order.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-[#1b5e20] flex-shrink-0 mt-0.5" />
                <span><strong>Operational Charges:</strong> ₹5 per order.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-[#1b5e20] flex-shrink-0 mt-0.5" />
                <span><strong>Payout Cycle:</strong> Every 6 days.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-[#1b5e20] flex-shrink-0 mt-0.5" />
                <span><strong>Menu Management:</strong> Menu add/edit/delete allowed, subject to Fudco admin approval.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-[#1b5e20] flex-shrink-0 mt-0.5" />
                <span><strong>Customer Data:</strong> Customer information is strictly confidential and may be used only for order fulfillment.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-[#1b5e20] flex-shrink-0 mt-0.5" />
                <span><strong>Food Responsibility:</strong> Restaurant is fully responsible for food quality, hygiene, packaging, and legal compliance.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-[#1b5e20] flex-shrink-0 mt-0.5" />
                <span><strong>Platform Rights:</strong> Fudco may suspend or modify listings for policy or quality violations.</span>
              </li>
            </ul>

            <p className="text-gray-600 text-sm">
              This summary forms part of the full Restaurant Partner Terms & Conditions available on Fudco.
            </p>
            
            <div className="p-4 bg-gray-100 rounded-xl">
              <div className="flex items-start gap-3">
                <Checkbox 
                  id="summary-agreement" 
                  checked={agreementAccepted}
                  onCheckedChange={setAgreementAccepted}
                  className="mt-1"
                  data-testid="restaurant-summary-checkbox"
                />
                <label htmlFor="summary-agreement" className="text-sm text-gray-700 cursor-pointer font-semibold">
                  I have read and agree to the Fudco Restaurant Partner Agreement Summary and the full Terms & Conditions.
                </label>
              </div>
            </div>
            
            <Button
              onClick={handleSummaryAccept}
              disabled={!agreementAccepted || loading}
              className="w-full h-12 bg-[#1b5e20] hover:bg-[#145216] text-white rounded-lg font-semibold"
              data-testid="restaurant-summary-accept-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Accept & Continue'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* OTP Verification Modal */}
      <Dialog open={showOtpModal} onOpenChange={setShowOtpModal}>
        <DialogContent className="sm:max-w-md" data-testid="restaurant-otp-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Verify Phone Number
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            <div className="text-center">
              <div className="bg-green-100 text-[#2D8B2E] p-4 rounded-full inline-block mb-4">
                <Phone className="w-8 h-8" />
              </div>
              <p className="text-gray-600 text-sm">
                OTP sent to <span className="font-semibold">+91 {formData.phone}</span>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="otp">Enter OTP</Label>
              <Input
                id="otp"
                type="text"
                value={otp}
                onChange={(e) => {
                  if (/^\d*$/.test(e.target.value)) {
                    setOtp(e.target.value);
                  }
                }}
                placeholder="Enter 6-digit OTP"
                maxLength={6}
                className="h-14 text-center text-2xl tracking-[0.5em] rounded-xl font-mono"
                data-testid="restaurant-otp-input"
              />
            </div>

            <Button
              onClick={handleVerifyOtp}
              disabled={otpLoading || otp.length !== 6}
              className="w-full h-12 bg-[#2D8B2E] hover:bg-[#236c24] text-white rounded-full font-semibold"
              data-testid="restaurant-verify-otp-btn"
            >
              {otpLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify & Continue'}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={countdown > 0}
                className={`text-sm ${countdown > 0 ? 'text-gray-400' : 'text-[#2D8B2E] hover:underline'}`}
                data-testid="restaurant-resend-otp-btn"
              >
                {countdown > 0 ? `Resend OTP in ${countdown}s` : 'Resend OTP'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
